import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../i18n';
import { isAdmin, isEditor, useStore } from '../store/store';
import { saveRecord } from './actions';
import { setData, useData } from './dataStore';
import { CategoryFilters } from './CategoryFilters';
import { CATEGORY_FILTERS, NO_FILTERS, passesFilters } from './filters';
import { useRecordsByGuid } from './hooks';
import { DataIcon } from './DataIcon';
import { Value } from './FieldView';
import { NewRecordDialog } from './NewRecordDialog';
import { ViewSwitch } from './ViewSwitch';
import { categoryIcon } from './categories';
import { allFields, classChain, fieldLabel, formatNumber, settingKey } from './schema';
import { cellText, columnStats, sortRecords, tableColumns, type Column, type Sort } from './table';
import { same } from './values';
import type { DataRecord } from './types';

// The balancing view: every record of a category side by side. Click a row to open it; editors can
// change numbers and checkboxes right in the table (saved like the form: validated, becomes a proposal).

const HIDDEN_KEY = 'mapeditor.table.hidden.';

function loadHidden(category: string): string[] {
  try {
    return JSON.parse(localStorage.getItem(HIDDEN_KEY + category) ?? '[]') as string[];
  } catch {
    return [];
  }
}

function saveHidden(category: string, hidden: string[]) {
  try {
    localStorage.setItem(HIDDEN_KEY + category, JSON.stringify(hidden));
  } catch {
    /* private mode etc. — a per-viewer convenience only */
  }
}

export function CategoryTable({ category }: { category: string }) {
  const { t, i18n } = useTranslation();
  const records = useData((s) => s.records);
  const classes = useData((s) => s.classes);
  const settings = useData((s) => s.settings);
  const cat = useData((s) => s.categories.find((c) => c.key === category));
  const user = useStore((s) => s.user);
  const [filter, setFilter] = useState('');
  const [sort, setSort] = useState<Sort>({ field: null, dir: 1 });
  const [hidden, setHidden] = useState<string[]>(() => loadHidden(category));
  const [creating, setCreating] = useState(false);

  const selection = useData((s) => s.filters[category]) ?? NO_FILTERS;
  const filterDefs = CATEGORY_FILTERS[category];

  const needle = filter.trim().toLowerCase();
  const rows = useMemo(
    () =>
      Object.values(records).filter(
        (r) =>
          r.category === category &&
          (!filterDefs || passesFilters(r, filterDefs, selection)) &&
          (!needle || r.name.toLowerCase().includes(needle) || r.key.toLowerCase().includes(needle)),
      ),
    [records, category, needle, filterDefs, selection],
  );
  const columns = useMemo(
    () => tableColumns(classes, settings, rows, cat?.nameField || undefined),
    [classes, settings, rows, cat?.nameField],
  );
  const shown = columns.filter((c) => !hidden.includes(c.field.name));
  const sorted = useMemo(() => sortRecords(rows, sort), [rows, sort]);
  const numeric = shown.filter((c) => c.numeric);
  const canEdit = isEditor(user) && !!cat?.editable;
  const label = (c: Column) =>
    fieldLabel(c.field, settings[settingKey(c.field.owner, c.field.name)], i18n.language);

  const toggleColumn = (name: string) => {
    const next = hidden.includes(name) ? hidden.filter((n) => n !== name) : [...hidden, name];
    setHidden(next);
    saveHidden(category, next);
  };
  const sortBy = (field: string | null) =>
    setSort((s) => (s.field === field ? { field, dir: s.dir === 1 ? -1 : 1 } : { field, dir: 1 }));
  const arrow = (field: string | null) => (sort.field === field ? (sort.dir === 1 ? ' ▲' : ' ▼') : '');

  return (
    <div className="table-view">
      <header className="table-head">
        <h2>{t(`data.categories.${category}`, { defaultValue: cat?.label ?? category })}</h2>
        <span className="muted small">{t('data.table.count', { count: rows.length })}</span>
        {filterDefs && <CategoryFilters category={category} filters={filterDefs} inline />}
        <div className="spacer" />
        <input
          className="table-filter"
          value={filter}
          placeholder={t('data.table.filter')}
          onChange={(e) => setFilter(e.target.value)}
        />
        <details className="col-picker">
          <summary className="small">
            {t('data.table.columns', { shown: shown.length, all: columns.length })}
          </summary>
          <div className="col-picker-list">
            {columns.map((c) => (
              <label key={c.field.owner + c.field.name} className="check-row small">
                <input
                  type="checkbox"
                  checked={!hidden.includes(c.field.name)}
                  onChange={() => toggleColumn(c.field.name)}
                />
                {label(c)}
              </label>
            ))}
          </div>
        </details>
        {canEdit && cat?.creatable && !cat.prefab && (
          <button className="small" onClick={() => setCreating(true)}>
            + {t('data.create.button')}
          </button>
        )}
        <ViewSwitch category={category} />
      </header>
      <p className="muted small">{canEdit ? t('data.table.helpEdit') : t('data.table.help')}</p>
      {creating && <NewRecordDialog category={category} onClose={() => setCreating(false)} />}
      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th className="sticky-col sortable" onClick={() => sortBy(null)}>
                {t('common.name')}
                {arrow(null)}
              </th>
              <th>{t('data.table.status')}</th>
              {shown.map((c) => (
                <th
                  key={c.field.owner + c.field.name}
                  className={`sortable${c.numeric ? ' num' : ''}`}
                  title={c.field.tooltip}
                  onClick={() => sortBy(c.field.name)}
                >
                  {label(c)}
                  {arrow(c.field.name)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <Row key={r.id} record={r} columns={shown} canEdit={canEdit} keyField={cat?.keyField ?? ''} />
            ))}
          </tbody>
          {numeric.length > 0 && sorted.length > 1 && (
            <tfoot>
              {(['min', 'median', 'max'] as const).map((k) => (
                <tr key={k}>
                  <th className="sticky-col muted small">{t(`data.table.${k}`)}</th>
                  <td />
                  {shown.map((c) => {
                    const st = c.numeric ? columnStats(sorted, c.field.name) : null;
                    return (
                      <td key={c.field.owner + c.field.name} className="num muted small">
                        {st ? formatNumber(st[k]) : ''}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function Row({
  record: r,
  columns,
  canEdit,
  keyField,
}: {
  record: DataRecord;
  columns: Column[];
  canEdit: boolean;
  keyField: string;
}) {
  const { t } = useTranslation();
  const admin = isAdmin(useStore((s) => s.user));
  const classes = useData((s) => s.classes);
  const editableRecord = canEdit && r.status !== 'deprecated';
  // A category can mix classes (items and food): a column this record's class doesn't have stays empty.
  const chain = classChain(classes, r.className);
  return (
    <tr onClick={() => setData({ selectedId: r.id })}>
      <th className="sticky-col">
        <span className="row">
          <DataIcon iconKey={r.icon} size={18} fallback={categoryIcon(r.category)} />
          <span className="ellipsis">{r.name}</span>
        </span>
      </th>
      <td>
        {(r.status !== 'in_unity' || r.syncState === 'missing_in_unity') && (
          <span
            className={`status-pill ${r.syncState === 'missing_in_unity' ? 'missing_in_unity' : r.status}`}
          >
            {r.syncState === 'missing_in_unity' ? t('sync.missing_in_unity') : t(`status.${r.status}`)}
          </span>
        )}
      </td>
      {columns.map((c) => {
        const name = c.field.name;
        if (!chain.includes(c.field.owner)) return <td key={c.field.owner + name} className="na" />;
        const locked = name === keyField && !!r.guid && !admin;
        const editable =
          editableRecord &&
          !locked &&
          c.visibility === 'edit' &&
          (c.field.kind === 'int' || c.field.kind === 'float' || c.field.kind === 'bool');
        return <Cell key={c.field.owner + name} record={r} column={c} editable={editable} />;
      })}
    </tr>
  );
}

function Cell({ record: r, column: c, editable }: { record: DataRecord; column: Column; editable: boolean }) {
  const { t } = useTranslation();
  const byGuid = useRecordsByGuid();
  const records = useData((s) => s.records);
  const name = c.field.name;
  const value = r.values?.[name];
  const unity = r.unityValues?.[name];
  const changed = !!r.guid && !same(value, unity);
  const refName = (ref: { guid?: string; new?: string }) =>
    ref.guid ? byGuid[ref.guid]?.name : ref.new ? records[ref.new]?.name : undefined;
  const title = changed ? `${t('data.edit.unityHas')} ${cellText(unity, refName)}` : undefined;
  const save = (v: unknown) => {
    if (same(v, value)) return;
    void saveRecord(r.id, r.rev, { values: { ...(r.values ?? {}), [name]: v } });
  };

  let content;
  if (editable && c.numeric) {
    content = (
      <input
        key={r.rev}
        type="number"
        className="cell-input"
        step={c.field.kind === 'int' ? 1 : 'any'}
        defaultValue={typeof value === 'number' ? value : ''}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') {
            (e.target as HTMLInputElement).value = typeof value === 'number' ? String(value) : '';
            (e.target as HTMLInputElement).blur();
          }
        }}
        onBlur={(e) => {
          const text = e.target.value.trim();
          const n = Number(text);
          if (text !== '' && isFinite(n)) save(n);
          else e.target.value = typeof value === 'number' ? String(value) : '';
        }}
      />
    );
  } else if (editable && c.field.kind === 'bool') {
    content = (
      <input
        type="checkbox"
        checked={value === true}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => save(e.target.checked)}
      />
    );
  } else if (c.numeric) {
    content = <span className="mono">{formatNumber(value)}</span>;
  } else if (c.field.kind === 'string') {
    content = <span className="ellipsis cell-text">{typeof value === 'string' && value ? value : '—'}</span>;
  } else {
    content = <Value field={c.field} value={value} />;
  }
  return (
    <td className={`${c.numeric ? 'num' : ''}${changed ? ' changed' : ''}`} title={title}>
      {content}
    </td>
  );
}

/** "Ändringar" as a table: everything waiting, with what changed, who changed it and whether it was sent. */
export function ChangesTable({ records }: { records: DataRecord[] }) {
  const { t, i18n } = useTranslation();
  const classes = useData((s) => s.classes);
  const settings = useData((s) => s.settings);
  const users = useStore((s) => s.users);
  const order = ['approved', 'proposed', 'idea', 'needs_update', 'in_unity'];
  const rows = [...records].sort(
    (a, b) => order.indexOf(a.status) - order.indexOf(b.status) || a.name.localeCompare(b.name, 'sv'),
  );
  const changedFields = (r: DataRecord) => {
    if (!r.guid) return t('data.create.newBadge');
    return allFields(classes, r.className)
      .filter((f) => !same(r.values?.[f.name], r.unityValues?.[f.name]))
      .map((f) => fieldLabel(f, settings[settingKey(f.owner, f.name)], i18n.language))
      .join(', ');
  };
  return (
    <div className="table-view">
      <header className="table-head">
        <h2>{t('data.changes')}</h2>
        <span className="muted small">{t('data.table.count', { count: rows.length })}</span>
      </header>
      {rows.length === 0 ? (
        <p className="muted">{t('data.table.noChanges')}</p>
      ) : (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th className="sticky-col">{t('common.name')}</th>
                <th>{t('data.table.category')}</th>
                <th>{t('data.table.status')}</th>
                <th>{t('data.table.what')}</th>
                <th>{t('data.table.who')}</th>
                <th>{t('data.table.sent')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} onClick={() => setData({ selectedId: r.id })}>
                  <th className="sticky-col">
                    <span className="row">
                      <DataIcon iconKey={r.icon} size={18} fallback={categoryIcon(r.category)} />
                      <span className="ellipsis">{r.name}</span>
                    </span>
                  </th>
                  <td>{t(`data.categories.${r.category}`, { defaultValue: r.category })}</td>
                  <td>
                    <span className={`status-pill ${r.syncState === 'conflict' ? 'conflict' : r.status}`}>
                      {r.syncState === 'conflict' ? t('sync.conflict') : t(`status.${r.status}`)}
                    </span>
                  </td>
                  <td className="cell-text">{changedFields(r)}</td>
                  <td>{users[r.changedBy]?.name ?? ''}</td>
                  <td className="small">
                    {r.exportedAt && r.status === 'approved' ? formatDate(r.exportedAt) : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
